import { useState } from 'react'
import { useAlterarDadosPessoais } from '../../api/queries/usuarios'
import { Botao } from '../../components/Botao'
import { AvisoDeErro } from '../../components/Estados'
import { CampoDeTexto } from '../../components/campos'
import { TEXTOS } from '../../lib/glossario'
import type { UsuarioLogado } from '../../types/gmud'

interface Props {
  usuario: UsuarioLogado
  /** Chamado ao salvar (`true`) ou ao desistir (`false`). */
  aoTerminar: (salvou: boolean) => void
}

/**
 * A correção do próprio nome e e-mail, no mesmo cartão onde eles aparecem.
 *
 * Pedido do Teste de uso 1. Antes disto não havia caminho nenhum — nem para a pessoa, nem para o
 * administrador — para corrigir um nome importado errado do sistema anterior.
 *
 * **A senha atual só aparece quando o e-mail muda.** O e-mail é o login, e trocá-lo sem a senha
 * deixaria uma sessão esquecida aberta tirar a conta da dona; o nome não tranca ninguém do lado de
 * fora. A comparação é a mesma do servidor — e-mail sem diferenciar maiúscula, nome sem os espaços
 * sobrando —, senão a tela pediria senha para uma troca que o servidor não vê como troca.
 *
 * @param props a conta logada e o que fazer ao terminar
 * @returns o formulário de correção
 */
export function EdicaoDosDadosPessoais({ usuario, aoTerminar }: Props) {
  const alterar = useAlterarDadosPessoais()
  const [nome, setNome] = useState(usuario.nome)
  const [email, setEmail] = useState(usuario.email)
  const [senhaAtual, setSenhaAtual] = useState('')
  const textos = TEXTOS.perfil.corrigir

  const trocaEmail = email.trim().toLowerCase() !== usuario.email.toLowerCase()
  const semMudanca = nome.trim().replace(/\s+/g, ' ') === usuario.nome && !trocaEmail
  const incompleto = !nome.trim() || !email.trim() || (trocaEmail && !senhaAtual)

  async function salvar() {
    if (semMudanca || incompleto) {
      return
    }
    try {
      await alterar.mutateAsync({ nome, email, ...(trocaEmail ? { senhaAtual } : {}) })
      aoTerminar(true)
    } catch {
      // O AvisoDeErro abaixo diz o que o servidor recusou e por quê.
    }
  }

  return (
    <form
      className="max-w-md space-y-4"
      noValidate
      onSubmit={(evento) => {
        evento.preventDefault()
        void salvar()
      }}
    >
      <CampoDeTexto
        rotulo={textos.nome}
        ajuda={textos.nomeAjuda}
        autoComplete="name"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
      />
      <CampoDeTexto
        rotulo={textos.email}
        ajuda={textos.emailAjuda}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      {trocaEmail && (
        <CampoDeTexto
          rotulo={textos.senhaAtual}
          ajuda={textos.senhaAtualAjuda}
          type="password"
          autoComplete="current-password"
          value={senhaAtual}
          onChange={(e) => setSenhaAtual(e.target.value)}
        />
      )}
      <p className="max-w-prose text-xs text-slate-500">{textos.organizacao}</p>

      {alterar.isError && <AvisoDeErro erro={alterar.error} titulo={textos.naoSalvou} />}

      <div className="flex flex-wrap gap-2">
        <Botao type="submit" aparencia="primario" disabled={alterar.isPending || semMudanca || incompleto}>
          {alterar.isPending ? 'Salvando…' : textos.salvar}
        </Botao>
        <Botao onClick={() => aoTerminar(false)} disabled={alterar.isPending}>
          {textos.cancelar}
        </Botao>
      </div>
    </form>
  )
}
